/** Path builders ported from WritingReferenceLetterTemplates.swift */

export const point = (x, y) => ({ x, y });

export function poly(coordinates, kind = "primary", closed = false) {
  return { points: coordinates.map(([x, y]) => point(x, y)), kind, isClosed: closed };
}

export function line(x0, y0, x1, y1, kind = "primary") {
  return poly([[x0, y0], [x1, y1]], kind);
}

export function cubic(x0, y0, x1, y1, x2, y2, x3, y3, kind = "primary", closed = false, samples = 18) {
  const points = [];
  for (let index = 0; index <= samples; index += 1) {
    const t = index / samples;
    const oneMinusT = 1 - t;
    points.push(
      point(
        oneMinusT * oneMinusT * oneMinusT * x0 +
          3 * oneMinusT * oneMinusT * t * x1 +
          3 * oneMinusT * t * t * x2 +
          t * t * t * x3,
        oneMinusT * oneMinusT * oneMinusT * y0 +
          3 * oneMinusT * oneMinusT * t * y1 +
          3 * oneMinusT * t * t * y2 +
          t * t * t * y3,
      ),
    );
  }
  return { points, kind, isClosed: closed };
}

export function arc(centerX, centerY, radiusX, radiusY, startDegrees, endDegrees, samples = 28) {
  const points = [];
  for (let index = 0; index <= samples; index += 1) {
    const progress = index / samples;
    const degrees = startDegrees + (endDegrees - startDegrees) * progress;
    const radians = (degrees * Math.PI) / 180;
    points.push(point(centerX + Math.cos(radians) * radiusX, centerY + Math.sin(radians) * radiusY));
  }
  return { points, kind: "primary", isClosed: false };
}

export function ellipse(centerX, centerY, radiusX, radiusY, samples = 36) {
  const points = [];
  for (let index = 0; index <= samples; index += 1) {
    const radians = -Math.PI / 2 + (2 * Math.PI * index) / samples;
    points.push(point(centerX + Math.cos(radians) * radiusX, centerY + Math.sin(radians) * radiusY));
  }
  return { points, kind: "primary", isClosed: true };
}

export function dot(centerX, centerY) {
  const radius = 0.025;
  const points = [];
  for (let index = 0; index <= 10; index += 1) {
    const radians = (2 * Math.PI * index) / 10;
    points.push(point(centerX + Math.cos(radians) * radius, centerY + Math.sin(radians) * radius));
  }
  return { points, kind: "dot", isClosed: true };
}

export function joined(pieces, kind = "primary", closed = false) {
  const points = [];
  for (const piece of pieces) {
    if (
      points.length &&
      piece.points[0] &&
      points[points.length - 1].x === piece.points[0].x &&
      points[points.length - 1].y === piece.points[0].y
    ) {
      points.push(...piece.points.slice(1));
    } else {
      points.push(...piece.points);
    }
  }
  return { points, kind, isClosed: closed };
}

/**
 * Sample an absolute SVG path (M, L, C, Q, A — the subset used by
 * src/data/glyphStrokes.js) into a polyline of {x, y} points. Pure JS so it
 * runs identically in node tests and in the browser.
 */
export function svgPathToPolyline(d, { curveSamples = 20, arcSamples = 28 } = {}) {
  const tokens = String(d || "")
    .replace(/,/g, " ")
    .match(/[MLCQAZmlcqaz]|-?\d*\.?\d+(?:e-?\d+)?/g);
  if (!tokens) return [];
  const points = [];
  let cursor = { x: 0, y: 0 };
  let startOfSubpath = { x: 0, y: 0 };
  let command = "M";
  let index = 0;
  const readNumber = () => Number(tokens[index++]);
  const push = (p) => {
    const last = points[points.length - 1];
    if (!last || last.x !== p.x || last.y !== p.y) points.push(p);
    cursor = p;
  };
  while (index < tokens.length) {
    const token = tokens[index];
    if (/[A-Za-z]/.test(token)) {
      command = token.toUpperCase();
      index += 1;
      if (command === "Z") {
        push(startOfSubpath);
        continue;
      }
    }
    if (command === "M") {
      const p = point(readNumber(), readNumber());
      startOfSubpath = p;
      push(p);
      command = "L";
    } else if (command === "L") {
      push(point(readNumber(), readNumber()));
    } else if (command === "C") {
      const [x1, y1, x2, y2, x3, y3] = [readNumber(), readNumber(), readNumber(), readNumber(), readNumber(), readNumber()];
      const piece = cubic(cursor.x, cursor.y, x1, y1, x2, y2, x3, y3, "primary", false, curveSamples);
      piece.points.slice(1).forEach(push);
    } else if (command === "Q") {
      const [x1, y1, x2, y2] = [readNumber(), readNumber(), readNumber(), readNumber()];
      const c1x = cursor.x + (2 / 3) * (x1 - cursor.x);
      const c1y = cursor.y + (2 / 3) * (y1 - cursor.y);
      const c2x = x2 + (2 / 3) * (x1 - x2);
      const c2y = y2 + (2 / 3) * (y1 - y2);
      const piece = cubic(cursor.x, cursor.y, c1x, c1y, c2x, c2y, x2, y2, "primary", false, curveSamples);
      piece.points.slice(1).forEach(push);
    } else if (command === "A") {
      const [rx, ry, rotation, largeArc, sweep, x, y] = [
        readNumber(), readNumber(), readNumber(), readNumber(), readNumber(), readNumber(), readNumber(),
      ];
      sampleArc(cursor, rx, ry, rotation, largeArc, sweep, point(x, y), arcSamples).slice(1).forEach(push);
    } else {
      index += 1;
    }
  }
  return points;
}

/** SVG endpoint-parameterised elliptical arc → sampled points (incl. both ends). */
function sampleArc(from, rxIn, ryIn, rotationDeg, largeArc, sweep, to, samples) {
  if (from.x === to.x && from.y === to.y) return [from];
  let rx = Math.abs(rxIn);
  let ry = Math.abs(ryIn);
  if (!rx || !ry) return [from, to];
  const phi = (rotationDeg * Math.PI) / 180;
  const cosPhi = Math.cos(phi);
  const sinPhi = Math.sin(phi);
  const dx = (from.x - to.x) / 2;
  const dy = (from.y - to.y) / 2;
  const x1 = cosPhi * dx + sinPhi * dy;
  const y1 = -sinPhi * dx + cosPhi * dy;
  const lambda = (x1 * x1) / (rx * rx) + (y1 * y1) / (ry * ry);
  if (lambda > 1) {
    rx *= Math.sqrt(lambda);
    ry *= Math.sqrt(lambda);
  }
  const sign = largeArc === sweep ? -1 : 1;
  const numerator = rx * rx * ry * ry - rx * rx * y1 * y1 - ry * ry * x1 * x1;
  const denominator = rx * rx * y1 * y1 + ry * ry * x1 * x1;
  const coefficient = sign * Math.sqrt(Math.max(0, numerator / Math.max(1e-12, denominator)));
  const cx1 = (coefficient * rx * y1) / ry;
  const cy1 = (-coefficient * ry * x1) / rx;
  const cx = cosPhi * cx1 - sinPhi * cy1 + (from.x + to.x) / 2;
  const cy = sinPhi * cx1 + cosPhi * cy1 + (from.y + to.y) / 2;
  const angle = (ux, uy, vx, vy) => {
    const dot = ux * vx + uy * vy;
    const len = Math.hypot(ux, uy) * Math.hypot(vx, vy);
    let a = Math.acos(Math.min(1, Math.max(-1, dot / Math.max(1e-12, len))));
    if (ux * vy - uy * vx < 0) a = -a;
    return a;
  };
  const theta1 = angle(1, 0, (x1 - cx1) / rx, (y1 - cy1) / ry);
  let delta = angle((x1 - cx1) / rx, (y1 - cy1) / ry, (-x1 - cx1) / rx, (-y1 - cy1) / ry);
  if (!sweep && delta > 0) delta -= Math.PI * 2;
  if (sweep && delta < 0) delta += Math.PI * 2;
  const out = [];
  for (let i = 0; i <= samples; i += 1) {
    const t = theta1 + (delta * i) / samples;
    const ex = rx * Math.cos(t);
    const ey = ry * Math.sin(t);
    out.push(point(cosPhi * ex - sinPhi * ey + cx, sinPhi * ex + cosPhi * ey + cy));
  }
  out[0] = from;
  out[out.length - 1] = to;
  return out;
}

export function variant(id, strokes, extras = {}) {
  return {
    id,
    strokes,
    expectedEnclosures: extras.enclosures || 0,
    expectedIntersections: extras.intersections || 0,
    expectedDetachedMarks: extras.detachedMarks || 0,
  };
}
